import React, { FC, useMemo } from 'react';
import { PageHeading, Skeleton, Table } from 'src/common';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { faChartLine } from '@fortawesome/pro-light-svg-icons';
import { ColumnProps } from 'antd/lib/table';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { useMyCompanyCompetitors } from 'src/models/company/Competitors/hooks';
import { IOrgWithPayments } from 'src/models/company/Competitors/types';

function useTableColumns(): ColumnProps<IOrgWithPayments>[] {
  const { t } = useTranslation();

  return useMemo(() => {
    const columns: ColumnProps<IOrgWithPayments>[] = [
      {
        title: t('Company.companyName'),
        dataIndex: 'orgName',
        className: styles.colName,
        ellipsis: true,
        render: (_, record) => {
          return (
            <>
              <span className={styles.orgColor} style={{ backgroundColor: record.color }}></span>
              {record.supplierOrgName}
            </>
          );
        }
      },
      {
        title: t('Company.orgNumber'),
        dataIndex: 'orgId',
        align: 'right',
        render(_, record) {
          return <>{record.supplierOrgId}</>;
        }
      }
    ];
    return columns;
  }, [t]);
}

interface IProps {}

export const CompetitorsSettings: FC<IProps> = () => {
  const { t } = useTranslation();
  const isCompetitorsFeature = useFeatureFlag(FeatureFlag.Users_Competitors);
  const { data: competitors, loading } = useMyCompanyCompetitors();
  const noCompetitors = useMemo(() => !competitors.length && !loading, [competitors, loading]);

  const columns: ColumnProps<IOrgWithPayments>[] = useTableColumns();

  return (
    <>
      <PageHeading
        title={t('Navigation.Settings.competitors')}
        desc={
          isCompetitorsFeature
            ? noCompetitors
              ? t('Competitors.noCompetitorsDesc')
              : t('Competitors.competitorsSettingsDesc')
            : t('Competitors.noAccessToCompetitorsDesc')
        }
        icon={faChartLine}
      />
      {isCompetitorsFeature && !noCompetitors && (
        <div className={styles.contentInner}>
          {loading ? (
            <Skeleton active loading />
          ) : (
            <>
              <h3 className={styles.contentTitle}>
                <span>{t('Common.details')}</span>
              </h3>
              <Table
                rowKey={'supplierOrgId'}
                columns={columns}
                dataSource={competitors}
                className={styles.table}
                size={'large'}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default CompetitorsSettings;
